import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCompact } from 'app/pages/Home/slice/selectors';
import { homeActions } from 'app/pages/Home/slice';
import { getUserPreferences, saveUserPreferences } from 'utils/helpers';
import { getDefaultExpandedMonths } from '../utils/getDefaultExpandedMonths';
import {
  monthlyTimelineActions,
  useMonthlyTimelineSlice
} from '../store/monthlyTimelineSlice';
import { selectExpandedMonths, selectMonthsInRange } from '../store/selectors';

export function useExpandedMonths() {
  const dispatch = useDispatch();
  useMonthlyTimelineSlice();
  const months = useSelector(selectMonthsInRange);
  const expandedMonths = useSelector(selectExpandedMonths);
  const isCompact = useSelector(selectCompact);

  useEffect(() => {
    if (isCompact) {
      dispatch(monthlyTimelineActions.collapseAllMonths());
      return;
    }

    const preferences = getUserPreferences();
    const monthKeys = new Set(months.map(month => month.key));

    if (
      preferences.expandedMonths &&
      Array.isArray(preferences.expandedMonths) &&
      preferences.expandedMonths.length > 0
    ) {
      const validStoredMonths = preferences.expandedMonths.filter(monthKey =>
        monthKeys.has(monthKey)
      );

      if (validStoredMonths.length > 0) {
        dispatch(
          monthlyTimelineActions.applyExpandedMonthsFromPrefs(validStoredMonths)
        );
        return;
      }
    }

    dispatch(
      monthlyTimelineActions.applyExpandedMonthsFromPrefs(
        getDefaultExpandedMonths(months)
      )
    );
  }, [dispatch, months, isCompact]);

  useEffect(() => {
    const handleRestoreMonths = (event: CustomEvent) => {
      const monthKeys = new Set(months.map(month => month.key));
      const requestedMonths = Array.isArray(event.detail?.expandedMonths)
        ? event.detail.expandedMonths
        : [];
      const validMonths = requestedMonths.filter((monthKey: string) =>
        monthKeys.has(monthKey)
      );

      dispatch(
        monthlyTimelineActions.applyExpandedMonthsFromPrefs(
          validMonths.length > 0
            ? validMonths
            : getDefaultExpandedMonths(months)
        )
      );
    };

    window.addEventListener(
      'restoreMonths',
      handleRestoreMonths as EventListener
    );
    return () => {
      window.removeEventListener(
        'restoreMonths',
        handleRestoreMonths as EventListener
      );
    };
  }, [dispatch, months]);

  const toggleMonthExpanded = useCallback(
    (monthKey: string) => {
      if (isCompact) {
        dispatch(homeActions.setCompact(false));
        dispatch(
          monthlyTimelineActions.applyExpandedMonthsFromPrefs([monthKey])
        );
        saveUserPreferences({
          isCompact: false,
          expandedMonths: [monthKey]
        });
        return;
      }

      const nextExpandedMonths = expandedMonths.includes(monthKey)
        ? expandedMonths.filter(key => key !== monthKey)
        : [...expandedMonths, monthKey];

      dispatch(
        monthlyTimelineActions.applyExpandedMonthsFromPrefs(nextExpandedMonths)
      );
      saveUserPreferences({ expandedMonths: nextExpandedMonths });
    },
    [dispatch, expandedMonths, isCompact]
  );

  const isMonthExpanded = useCallback(
    (monthKey: string) => expandedMonths.includes(monthKey),
    [expandedMonths]
  );

  return {
    expandedMonths,
    toggleMonthExpanded,
    isMonthExpanded
  };
}
